<template>
  <v-card flat class="external-product-images-load mt-4">
    <v-card-title>Analizar y preparar imágenes de productos</v-card-title>
    <v-card-text>
      <v-alert v-if="!canManageImages" type="warning" dense>
        No tiene permiso para analizar o preparar cargas de imágenes.
      </v-alert>
      <template v-else>
        <v-alert type="info" outlined dense>
          Seleccione archivos individuales o una carpeta. Primero se analizan y reservan nombres; la carga a S3 se ejecuta después desde el botón correspondiente. Límite técnico por archivo: 50 MB.
        </v-alert>
        <v-row class="external-product-images-load__selector-row">
          <v-col cols="12" md="6" class="external-product-images-load__selector-col">
            <input
              id="external-product-images-folder"
              ref="folderInput"
              class="d-none"
              type="file"
              webkitdirectory
              directory
              multiple
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              @change="handleSelection"
            />
            <label for="external-product-images-folder" class="external-product-images-load__selector">
              <v-icon left>mdi-folder-open</v-icon>Seleccionar carpeta
            </label>
          </v-col>
          <v-col cols="12" md="6" class="external-product-images-load__selector-col">
            <input
              id="external-product-images-files"
              ref="filesInput"
              class="d-none"
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
              @change="handleSelection"
            />
            <label for="external-product-images-files" class="external-product-images-load__selector">
              <v-icon left>mdi-file-multiple</v-icon>Seleccionar archivos
            </label>
          </v-col>
        </v-row>
        <div v-if="selectedFiles.length" class="caption external-product-images-load__summary">
          {{ selectedFiles.length }} archivo(s) seleccionado(s).
        </div>
        <div class="external-product-images-load__actions">
          <v-btn color="primary" :loading="analyzing" :disabled="!selectedFiles.length || preparing || uploading" @click="analyzeFiles">
            Analizar selección
          </v-btn>
          <v-btn :disabled="analyzing || preparing || uploading" @click="resetSelection">
            Limpiar
          </v-btn>
        </div>

        <v-progress-linear v-if="analyzing || preparing" class="my-4" indeterminate color="primary" />
        <v-alert v-if="analysisError" type="error" dense class="mt-4">{{ analysisError }}</v-alert>
        <v-alert v-if="analysisSummary" type="info" dense class="mt-4">
          Total: {{ analysisSummary.total }} · Válidas: {{ analysisSummary.VALIDATED }} · No identificadas: {{ analysisSummary.UNIDENTIFIED }} · Ambiguas: {{ analysisSummary.AMBIGUOUS }} · Inválidas: {{ analysisSummary.INVALID }}
        </v-alert>
        <v-alert v-if="omittedFiles" type="success" dense class="mt-4">
          Se omitieron {{ omittedFiles }} archivo(s) sin identificación válida. No se asociarán ni se reservarán nombres para ellos.
        </v-alert>
        <v-btn
          v-if="hasNonValidatedFiles"
          class="mt-3"
          color="warning"
          outlined
          :disabled="analyzing || preparing"
          @click="omitNonValidatedFiles"
        >
          Omitir archivos no válidos
        </v-btn>

        <v-simple-table v-if="paginatedAnalyzedFiles.length" dense class="mt-4">
          <thead>
            <tr>
              <th>Archivo</th>
              <th>Producto</th>
              <th>Estado</th>
              <th>Destino estimado</th>
              <th>Estado S3</th>
              <th>Asociación</th>
              <th>Detalle</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="file in paginatedAnalyzedFiles" :key="file.relativeSourcePath">
              <td>{{ file.relativeSourcePath }}</td>
              <td>{{ file.product ? `${file.product.id} - ${file.product.name}` : '—' }}</td>
              <td>{{ statusLabel(file.status) }}</td>
              <td>
                <span v-if="file.destinationKey">{{ file.destinationKey }} (reservado)</span>
                <span v-else-if="file.estimatedDestinationKey">{{ file.estimatedDestinationKey }} (estimado)</span>
                <span v-else>—</span>
              </td>
              <td>{{ s3StatusLabel(file.s3Status) }}</td>
              <td>{{ associationStatusLabel(file.associationStatus) }}</td>
              <td>{{ [file.validationMessage, ...(file.warnings || [])].filter(Boolean).join(' ') || '—' }}</td>
            </tr>
          </tbody>
        </v-simple-table>
        <v-row v-if="analyzedFiles.length" class="external-product-images-load__pagination align-center">
          <v-col cols="12" sm="3" md="2">
            <v-select
              v-model="itemsPerPage"
              :items="itemsPerPageOptions"
              label="Por página"
              dense
              outlined
              hide-details
            />
          </v-col>
          <v-col cols="12" sm="9" md="10" class="d-flex justify-end">
            <v-pagination
              v-model="analysisPage"
              :length="analysisPageCount"
              :total-visible="7"
              circle
            />
          </v-col>
        </v-row>

        <div v-if="groups.length" class="mt-6">
          <div class="subtitle-1 mb-2">Orden por producto</div>
          <v-card v-for="group in groups" :key="group.product.id" outlined class="mb-3">
            <v-card-title class="subtitle-1 py-2">
              {{ group.product.id }} - {{ group.product.name }}
              <span v-if="hasReservedManifest" class="caption ml-3">{{ groupUploadLabel(group) }}</span>
            </v-card-title>
            <v-list dense>
              <v-list-item v-for="(file, index) in group.files" :key="file.relativeSourcePath">
                <v-list-item-content>
                  <v-list-item-title>{{ index + 1 }}. {{ file.relativeSourcePath }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ file.destinationKey || file.estimatedDestinationKey || 'La key se asignará al confirmar' }}
                    <span v-if="!file.destinationKey"> · estimado</span>
                  </v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="d-flex flex-row">
                  <v-btn icon small :disabled="index === 0 || preparing || hasReservedManifest" @click="moveImage(group, index, -1)"><v-icon small>mdi-chevron-up</v-icon></v-btn>
                  <v-btn icon small :disabled="index === group.files.length - 1 || preparing || hasReservedManifest" @click="moveImage(group, index, 1)"><v-icon small>mdi-chevron-down</v-icon></v-btn>
                  <v-btn icon small color="error" :disabled="preparing || hasReservedManifest" @click="removeImage(group, index)"><v-icon small>mdi-close</v-icon></v-btn>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
          <v-btn v-if="!hasReservedManifest" color="success" :loading="preparing" :disabled="!canPrepare || analyzing || uploading" @click="prepareBatch">
            Confirmar y reservar nombres
          </v-btn>
          <v-btn v-else-if="!allS3Uploaded" color="primary" :loading="uploading" :disabled="!canUpload" class="ml-2" @click="uploadReservedFiles">
            Subir imágenes a S3
          </v-btn>
          <v-btn v-else color="success" :loading="associating" :disabled="!canAssociate" class="ml-2" @click="associateReservedFiles">
            Asociar imágenes
          </v-btn>
          <v-alert v-if="allS3Uploaded && analyzedFiles.some((file) => file.associationStatus !== 'ASSOCIATED')" type="warning" outlined dense class="mt-4">
            La asociación reemplazará en la plataforma externa la lista de imágenes de cada producto por las imágenes confirmadas en esta carga.
          </v-alert>
          <v-alert v-if="uploadSummary" type="info" outlined dense class="mt-4">
            S3: {{ uploadSummary.uploaded }} subidas · {{ uploadSummary.failed }} rechazadas · {{ uploadSummary.unknown }} inciertas · {{ uploadSummary.pending }} pendientes de {{ uploadSummary.total }}.
            La asociación externa permanece pendiente hasta el siguiente entregable.
          </v-alert>
          <v-progress-linear v-if="uploading" class="mt-3" color="primary" :value="uploadProgress" height="8" />
          <v-alert v-if="uploadError" type="error" dense class="mt-3">{{ uploadError }}</v-alert>
          <v-alert v-if="associationSummary" type="info" outlined dense class="mt-3">
            Asociación: {{ associationSummary.associated }} productos actualizados · {{ associationSummary.failed }} con error · {{ associationSummary.unknown }} inciertos de {{ associationSummary.total }}.
          </v-alert>
          <v-alert v-if="associationError" type="error" dense class="mt-3">{{ associationError }}</v-alert>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const ALLOWED_EXTENSIONS = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp']
};

export default {
  name: 'ManageExternalProductImagesLoad',
  props: {
    canManageImages: { type: Boolean, default: false }
  },
  data() {
    return {
      selectedFiles: [],
      analyzedFiles: [],
      groups: [],
      analysisSummary: null,
      analysisError: '',
      analyzing: false,
      preparing: false,
      uploading: false,
      uploadProgress: 0,
      uploadError: '',
      uploadStopped: false,
      batchId: null,
      uploadSummary: null,
      associating: false,
      associationError: '',
      associationSummary: null,
      requestToken: '',
      omittedFiles: 0,
      analysisPage: 1,
      itemsPerPage: 10,
      itemsPerPageOptions: [10, 25, 50]
    };
  },
  computed: {
    canPrepare() {
      return this.groups.length > 0 && this.analyzedFiles.length > 0 && !this.hasNonValidatedFiles && !this.hasReservedManifest;
    },
    hasNonValidatedFiles() {
      return this.analyzedFiles.some((file) => file.status !== 'VALIDATED');
    },
    hasReservedManifest() {
      return this.analyzedFiles.some((file) => file.ID_Image && file.destinationKey);
    },
    canUpload() {
      return Boolean(this.batchId)
        && this.hasReservedManifest
        && !this.uploading
        && !this.analyzedFiles.some((file) => file.s3Status === 'UNKNOWN')
        && Boolean(this.uploadSummary && (this.uploadSummary.pending + this.uploadSummary.failed > 0));
    },
    allS3Uploaded() {
      return Boolean(this.uploadSummary && this.uploadSummary.total > 0 && this.uploadSummary.uploaded === this.uploadSummary.total);
    },
    canAssociate() {
      return this.allS3Uploaded
        && Boolean(this.batchId)
        && !this.associating
        && !this.analyzedFiles.some((file) => file.associationStatus === 'UNKNOWN')
        && this.analyzedFiles.some((file) => file.associationStatus !== 'ASSOCIATED');
    },
    analysisPageCount() {
      return Math.max(1, Math.ceil(this.analyzedFiles.length / this.itemsPerPage));
    },
    paginatedAnalyzedFiles() {
      const start = (this.analysisPage - 1) * this.itemsPerPage;
      return this.analyzedFiles.slice(start, start + this.itemsPerPage);
    }
  },
  methods: {
    errorMessage(error, fallback) {
      return error?.response?.data?.message || error?.message || fallback;
    },
    normalizePath(value) {
      return String(value || '').replace(/\\/g, '/').replace(/^\/+/, '');
    },
    getRelativePath(file) {
      return this.normalizePath(file.webkitRelativePath || file.name);
    },
    getCandidates(filePath) {
      const segments = this.normalizePath(filePath).split('/').filter(Boolean);
      const candidates = [];
      segments.forEach((segment, index) => {
        const value = index === segments.length - 1 ? segment.replace(/\.[^.]+$/, '') : segment;
        if (value) candidates.push(value);
        if (index === segments.length - 1) {
          const parts = value.split(/[_-]/);
          if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1])) {
            candidates.push(parts.slice(0, -1).join('_'));
          }
        }
      });
      return [...new Set(candidates)];
    },
    detectContentType(buffer) {
      const bytes = new Uint8Array(buffer);
      if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return 'image/jpeg';
      const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
      if (bytes.length >= 8 && pngSignature.every((byte, index) => bytes[index] === byte)) return 'image/png';
      if (bytes.length >= 12 && String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP') return 'image/webp';
      return '';
    },
    readSignature(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(this.detectContentType(reader.result));
        reader.onerror = () => resolve('');
        reader.readAsArrayBuffer(file.slice(0, 16));
      });
    },
    async buildFileMetadata(file) {
      const contentType = await this.readSignature(file);
      const extension = file.name.split('.').pop().toLowerCase();
      const extensionMatches = Boolean(contentType && ALLOWED_EXTENSIONS[contentType]?.includes(extension));
      const contentValid = Boolean(contentType && ALLOWED_TYPES.includes(contentType) && extensionMatches);
      return {
        originalFileName: file.name,
        relativeSourcePath: this.getRelativePath(file),
        fileSize: file.size,
        contentType,
        contentValid,
        validationMessage: !contentType
          ? 'El contenido no corresponde a JPEG, PNG o WEBP'
          : !extensionMatches
            ? 'La extensión no coincide con el contenido de la imagen'
            : '',
        candidates: this.getCandidates(this.getRelativePath(file))
      };
    },
    handleSelection(event) {
      const files = Array.from(event.target.files || []);
      this.selectedFiles = files;
      this.analyzedFiles = [];
      this.groups = [];
      this.analysisSummary = null;
      this.analysisError = '';
      this.requestToken = '';
      this.batchId = null;
      this.uploadSummary = null;
      this.uploadError = '';
      this.uploadStopped = false;
      this.uploadProgress = 0;
      this.associationError = '';
      this.associationSummary = null;
      this.omittedFiles = 0;
      this.analysisPage = 1;
      event.target.value = '';
    },
    async analyzeFiles() {
      this.analyzing = true;
      this.analysisError = '';
      try {
        const files = await Promise.all(this.selectedFiles.map((file) => this.buildFileMetadata(file)));
        const response = await this.$API.externalProductImages.analyzeFiles({ files });
        this.analyzedFiles = response.data?.files || [];
        this.analysisSummary = response.data?.summary || null;
        this.omittedFiles = 0;
        this.analysisPage = 1;
        this.requestToken = `${Date.now()}-${Math.random().toString(36).slice(2)}-${this.analyzedFiles.length}`;
        this.batchId = null;
        this.uploadSummary = null;
        this.uploadError = '';
        this.uploadStopped = false;
        this.uploadProgress = 0;
        this.associationError = '';
        this.associationSummary = null;
        this.buildGroups();
      } catch (error) {
        this.analysisError = this.errorMessage(error, 'No se pudo analizar la selección');
      } finally {
        this.analyzing = false;
      }
    },
    buildGroups() {
      const grouped = {};
      this.analyzedFiles.filter((file) => file.status === 'VALIDATED').forEach((file) => {
        const id = file.product.id;
        if (!grouped[id]) grouped[id] = { product: file.product, files: [] };
        grouped[id].files.push(file);
      });
      Object.values(grouped).forEach((group) => {
        if (group.files.some((file) => file.imageOrder)) {
          group.files.sort((left, right) => (left.imageOrder || 0) - (right.imageOrder || 0));
        }
      });
      this.groups = Object.values(grouped);
      this.syncImageOrder();
    },
    omitNonValidatedFiles() {
      const validPaths = new Set(
        this.analyzedFiles
          .filter((file) => file.status === 'VALIDATED')
          .map((file) => file.relativeSourcePath)
      );
      const omittedCount = this.analyzedFiles.length - validPaths.size;
      this.analyzedFiles = this.analyzedFiles.filter((file) => validPaths.has(file.relativeSourcePath));
      this.selectedFiles = this.selectedFiles.filter((file) => validPaths.has(this.getRelativePath(file)));
      this.omittedFiles += omittedCount;
      this.analysisSummary = this.buildSummary(this.analyzedFiles);
      this.analysisPage = 1;
      this.buildGroups();
    },
    buildSummary(files) {
      return files.reduce((result, file) => {
        result.total += 1;
        result[file.status] = (result[file.status] || 0) + 1;
        return result;
      }, { total: 0, VALIDATED: 0, UNIDENTIFIED: 0, AMBIGUOUS: 0, INVALID: 0 });
    },
    syncImageOrder() {
      this.groups.forEach((group) => group.files.forEach((file, index) => { file.imageOrder = index + 1; }));
    },
    moveImage(group, index, direction) {
      const target = index + direction;
      if (target < 0 || target >= group.files.length) return;
      const moved = group.files.splice(index, 1)[0];
      group.files.splice(target, 0, moved);
      this.syncImageOrder();
    },
    removeImage(group, index) {
      const removed = group.files.splice(index, 1)[0];
      this.analyzedFiles = this.analyzedFiles.filter((file) => file.relativeSourcePath !== removed.relativeSourcePath);
      this.selectedFiles = this.selectedFiles.filter((file) => this.getRelativePath(file) !== removed.relativeSourcePath);
      this.groups = this.groups.filter((item) => item.files.length);
      this.syncImageOrder();
    },
    async prepareBatch() {
      if (!this.canPrepare) return;
      this.preparing = true;
      this.analysisError = '';
      try {
        const files = this.groups.flatMap((group) => group.files.map((file) => ({
          ...file,
          productId: group.product.id,
          imageOrder: file.imageOrder
        })));
        const response = await this.$API.externalProductImages.prepareBatch({ requestToken: this.requestToken, files });
        this.batchId = response.data?.ID_Batch || null;
        const preparedByPath = new Map((response.data?.items || []).map((item) => [item.relativeSourcePath, item]));
        this.analyzedFiles = this.analyzedFiles.map((file) => {
          const prepared = preparedByPath.get(file.relativeSourcePath);
          return prepared ? { ...file, ...prepared } : file;
        });
        this.buildGroups();
        this.updateUploadSummary();
        this.associationError = '';
        this.associationSummary = null;
        this.$toasted.success(response.data?.alreadyPrepared ? 'El manifiesto ya estaba reservado' : 'Manifiesto reservado correctamente');
      } catch (error) {
        this.analysisError = this.errorMessage(error, 'No se pudo reservar el manifiesto');
      } finally {
        this.preparing = false;
      }
    },
    getReservedFiles() {
      return this.groups.flatMap((group) => group.files);
    },
    getSelectedFile(relativeSourcePath) {
      return this.selectedFiles.find((file) => this.getRelativePath(file) === relativeSourcePath);
    },
    updateUploadSummary() {
      const files = this.getReservedFiles();
      this.uploadSummary = files.reduce((summary, file) => {
        summary.total += 1;
        if (file.s3Status === 'UPLOADED') summary.uploaded += 1;
        else if (file.s3Status === 'FAILED') summary.failed += 1;
        else if (file.s3Status === 'UNKNOWN') summary.unknown += 1;
        else summary.pending += 1;
        return summary;
      }, { total: 0, uploaded: 0, failed: 0, unknown: 0, pending: 0 });
    },
    groupUploadLabel(group) {
      const uploaded = group.files.filter((file) => file.s3Status === 'UPLOADED').length;
      const unknown = group.files.filter((file) => file.s3Status === 'UNKNOWN').length;
      const associated = group.files.filter((file) => file.associationStatus === 'ASSOCIATED').length;
      if (unknown) return `${uploaded}/${group.files.length} subidas · resultado incierto`;
      if (associated) return `${uploaded}/${group.files.length} subidas · ${associated} asociadas`;
      return `${uploaded}/${group.files.length} subidas`;
    },
    applyUploadResult(file, response) {
      const result = response?.data || {};
      this.$set(file, 's3Status', result.s3Status || 'FAILED');
      if (result.destinationKey) this.$set(file, 'destinationKey', result.destinationKey);
      if (result.s3Status === 'FAILED') {
        const detail = [result.errorCode, result.errorStatusCode].filter(Boolean).join(' / ');
        this.uploadError = detail
          ? `S3 rechazó el archivo (${detail}): ${result.errorMessage || response?.message || 'sin detalle adicional'}`
          : (response?.message || 'S3 rechazó el archivo');
      }
      this.updateUploadSummary();
    },
    async uploadReservedFiles() {
      if (!this.canUpload) return;
      this.uploading = true;
      this.uploadError = '';
      this.uploadStopped = false;
      this.uploadProgress = 0;
      const files = this.getReservedFiles();
      try {
        for (let index = 0; index < files.length; index += 1) {
          const reservedFile = files[index];
          if (reservedFile.s3Status === 'UPLOADED') continue;
          if (reservedFile.s3Status === 'UNKNOWN') {
            this.uploadStopped = true;
            break;
          }
          const sourceFile = this.getSelectedFile(reservedFile.relativeSourcePath);
          if (!sourceFile) {
            this.$set(reservedFile, 's3Status', 'FAILED');
            this.updateUploadSummary();
            continue;
          }
          const formData = new FormData();
          formData.append('file', sourceFile, sourceFile.name);
          formData.append('idImage', String(reservedFile.ID_Image));
          formData.append('relativeSourcePath', reservedFile.relativeSourcePath);
          try {
            const response = await this.$API.externalProductImages.uploadBatchFile(
              this.batchId,
              formData,
              (event) => {
                const current = event.total ? (event.loaded / event.total) : 0;
                this.uploadProgress = Math.round(((index + current) / files.length) * 100);
              }
            );
            this.applyUploadResult(reservedFile, response);
            if (response?.data?.s3Status === 'UNKNOWN') {
              this.uploadStopped = true;
              break;
            }
          } catch (error) {
            const response = error?.response?.data;
            if (response?.data?.s3Status) {
              this.applyUploadResult(reservedFile, response);
              if (response.data.s3Status === 'UNKNOWN') {
                this.uploadStopped = true;
                break;
              }
            } else if (!error?.response || ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error?.code) || error?.message === 'Network Error') {
              this.$set(reservedFile, 's3Status', 'UNKNOWN');
              this.uploadStopped = true;
              this.uploadError = 'No se pudo confirmar la respuesta de la carga. El archivo quedó incierto y no se reintentará automáticamente.';
              this.updateUploadSummary();
              break;
            } else {
              this.$set(reservedFile, 's3Status', 'FAILED');
              this.uploadError = this.errorMessage(error, 'No se pudo cargar una imagen');
              this.updateUploadSummary();
            }
          }
        }
        this.updateUploadSummary();
        this.uploadProgress = this.uploadSummary?.total
          ? Math.round((this.uploadSummary.uploaded / this.uploadSummary.total) * 100)
          : 0;
        if (this.uploadStopped) {
          this.uploadError = 'La carga se detuvo porque una imagen quedó en estado incierto. Debe conciliarla antes de reintentar.';
        } else if (this.uploadSummary?.failed) {
          this.uploadError = 'Algunas imágenes fueron rechazadas; puede corregirlas y volver a intentar las fallidas.';
        } else {
          this.$toasted.success('Carga a S3 procesada correctamente');
        }
      } finally {
        this.uploading = false;
      }
    },
    async associateReservedFiles() {
      if (!this.canAssociate) return;
      this.associating = true;
      this.associationError = '';
      try {
        const response = await this.$API.externalProductImages.associateBatch(this.batchId);
        const itemsByImage = new Map((response.data?.items || []).map((item) => [String(item.ID_Image), item]));
        this.analyzedFiles.forEach((file) => {
          const result = itemsByImage.get(String(file.ID_Image));
          if (!result) return;
          this.$set(file, 'associationStatus', result.associationStatus || 'UNKNOWN');
          this.$set(file, 'providerStatus', result.providerStatus || 'unknown');
          this.$set(file, 'associationMessage', result.message || '');
        });
        this.associationSummary = response.data?.summary || null;
        if (this.associationSummary?.unknown) {
          this.associationError = 'La respuesta de la asociación quedó incierta. No se reintentará automáticamente.';
        } else if (this.associationSummary?.failed) {
          this.associationError = 'Algunos productos no fueron asociados; puede revisar el resultado y reintentar los definitivos.';
        } else if (response.success) {
          this.$toasted.success('Imágenes asociadas correctamente');
        }
      } catch (error) {
        this.associationError = this.errorMessage(error, 'No se pudo procesar la asociación');
      } finally {
        this.associating = false;
      }
    },
    resetSelection() {
      this.selectedFiles = [];
      this.analyzedFiles = [];
      this.groups = [];
      this.analysisSummary = null;
      this.analysisError = '';
      this.requestToken = '';
      this.batchId = null;
      this.uploadSummary = null;
      this.uploadError = '';
      this.uploadStopped = false;
      this.uploadProgress = 0;
      this.associationError = '';
      this.associationSummary = null;
      this.omittedFiles = 0;
      this.analysisPage = 1;
      if (this.$refs.folderInput) this.$refs.folderInput.value = '';
      if (this.$refs.filesInput) this.$refs.filesInput.value = '';
    },
    statusLabel(status) {
      return {
        VALIDATED: 'Válida',
        UNIDENTIFIED: 'No identificada',
        AMBIGUOUS: 'Ambigua',
        INVALID: 'Inválida'
      }[status] || status;
    },
    s3StatusLabel(status) {
      return {
        PENDING: 'Pendiente',
        UPLOADING: 'Cargando',
        UPLOADED: 'Confirmada',
        FAILED: 'Rechazada',
        UNKNOWN: 'Incierta'
      }[status] || '—';
    },
    associationStatusLabel(status) {
      return {
        PENDING: 'Pendiente',
        ASSOCIATING: 'Procesando',
        ASSOCIATED: 'Asociada',
        FAILED: 'Error',
        UNKNOWN: 'Incierta'
      }[status] || '—';
    }
  }
};
</script>

<style scoped>
.external-product-images-load {
  background-color: #ffffff !important;
  border-top: 1px solid #e0e0e0;
}

.external-product-images-load .v-card__title {
  padding: 18px 16px 10px;
}

.external-product-images-load .v-card__text {
  padding: 8px 16px 20px;
}

.external-product-images-load__summary {
  margin: 12px 0 0;
  min-height: 20px;
  line-height: 20px;
  color: #5f6368;
}

.external-product-images-load__selector-row {
  margin: 0 !important;
}

.external-product-images-load__selector-col {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.external-product-images-load__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.external-product-images-load__selector {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  width: 100%;
  border: 1px solid #9e9e9e;
  border-radius: 4px;
  cursor: pointer;
}

.external-product-images-load__selector:hover {
  border-color: #1976d2;
}

.external-product-images-load__pagination {
  margin: 4px 0 0 !important;
}

.external-product-images-load__pagination .v-pagination {
  justify-content: flex-end;
}

@media (max-width: 600px) {
  .external-product-images-load__actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}
</style>
